import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CookieRoundedIcon from "@mui/icons-material/CookieRounded";
import { showCookiePreferences } from "../cookieConsent";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useContext, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColorModeContext } from "../colorMode";

export function LanguageSelect({ compact = false }: { compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const currentLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "de"
  ).split("-")[0];
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const languageItems = [
    { value: "de", label: t("common.language.de") },
    { value: "en", label: t("common.language.en") },
    { value: "fr", label: t("common.language.fr") },
    { value: "es", label: t("common.language.es") },
  ];

  return (
    <>
      <IconButton
        aria-label={t("common.language.label")}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        color="inherit"
        size={compact ? "small" : "medium"}
        aria-controls={menuOpen ? menuId : undefined}
        aria-haspopup="menu"
        aria-expanded={menuOpen ? "true" : undefined}
      >
        <LanguageRoundedIcon />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {languageItems.map((item) => {
          const isSelected = currentLanguage === item.value;
          return (
            <MenuItem
              key={item.value}
              selected={isSelected}
              onClick={() => {
                i18n.changeLanguage(item.value);
                setAnchorEl(null);
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {isSelected ? (
                  <CheckRoundedIcon fontSize="small" />
                ) : (
                  <Box sx={{ width: 18 }} />
                )}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default function Navigation() {
  const { t } = useTranslation();
  const colorMode = useContext(ColorModeContext);
  const theme = useTheme();
  const { pathname } = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navItems = useMemo(
    () => [
      { key: "home", label: t("common.nav.home"), to: "/" },
      { key: "about", label: t("common.nav.about"), to: "/ueber-uns" },
      { key: "services", label: t("common.nav.services"), to: "/leistungen" },
      { key: "faq", label: "FAQ", to: "/haeufige-fragen" },
      {
        key: "testimonials",
        label: t("common.nav.testimonials"),
        to: "/referenzen",
      },
      { key: "contact", label: t("common.nav.contact"), to: "/kontakt" },
      {
        key: "appointment",
        label: t("common.nav.appointment"),
        to: "/termin-vereinbaren",
      },
      { key: "quote", label: t("common.nav.quote"), to: "/angebot" },
    ],
    [t],
  );
  const themeToggleLabel =
    colorMode?.mode === "dark"
      ? t("common.nav.lightMode")
      : t("common.nav.darkMode");
  const isNavItemActive = (to: string) => {
    if (to === "/") return pathname === "/";
    if (to === "/leistungen") return pathname === "/leistungen" || pathname.startsWith("/leistungen/");
    if (to === "/haeufige-fragen") return pathname === "/haeufige-fragen" || pathname === "/faq";
    if (to === "/referenzen") return pathname === "/referenzen" || pathname === "/kundenbewertungen";
    return pathname === to;
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background:
          colorMode?.mode === "dark"
            ? "rgba(11, 21, 20, 0.85)"
            : "rgba(244, 239, 230, 0.85)",
        backdropFilter: "blur(12px)",
        color: "inherit",
        py: 1,
        top: 0,
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", gap: 2 }}>
        <Box
          component={RouterLink}
          to="/"
          sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}
        >
          <img
            src={colorMode?.mode === "dark" ? "/logo_transparent_darkmode.png" : "/logo_transparent.png"}
            alt={t("common.brand.name")}
            style={{
              height: 48,
              width: "auto",
              objectFit: "contain",
            }}
          />
        </Box>
        {isMobile ? (
          <IconButton
            aria-label={t("common.nav.menu")}
            onClick={() => setDrawerOpen(true)}
            color="inherit"
          >
            <MenuRoundedIcon />
          </IconButton>
        ) : (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              flexWrap: "wrap",
              rowGap: 0.75,
              columnGap: 0.75,
              justifyContent: "flex-end",
            }}
          >
            {navItems.map((item) => (
              (() => {
                const active = isNavItemActive(item.to);
                const isQuote = item.key === "quote";
                return (
                  <Button
                    key={item.key}
                    component={RouterLink}
                    to={item.to}
                    color={isQuote ? "secondary" : active ? "primary" : "inherit"}
                    variant={isQuote || active ? "contained" : "text"}
                    size="small"
                    sx={{
                      px: isQuote ? 2 : 1.25,
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                      fontWeight: active ? 700 : 500,
                      ...(active && !isQuote
                        ? {
                            boxShadow: "0 0 0 1px color-mix(in srgb, var(--accent) 50%, transparent)",
                          }
                        : null),
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })()
            ))}
            <IconButton
              color="inherit"
              size="small"
              onClick={() => colorMode?.toggleMode()}
              aria-label={themeToggleLabel}
            >
              {colorMode?.mode === "dark" ? (
                <LightModeRoundedIcon />
              ) : (
                <DarkModeRoundedIcon />
              )}
            </IconButton>
            <Tooltip title={t("common.nav.cookieSettings")}>
              <IconButton color="inherit" size="small" aria-label={t("common.nav.cookieSettings")}
                onClick={() => { void showCookiePreferences(); }}>
                <CookieRoundedIcon />
              </IconButton>
            </Tooltip>
            <LanguageSelect />
          </Stack>
        )}
      </Toolbar>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Stack spacing={1} sx={{ mb: 2, alignItems: "flex-start" }}>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <img
                src={colorMode?.mode === "dark" ? "/logo_transparent_darkmode.png" : "/logo_transparent.png"}
                alt={t("common.brand.name")}
                style={{
                  height: 40,
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Stack>
          <List>
            {navItems.map((item) => (
              <ListItemButton
                key={item.key}
                component={RouterLink}
                to={item.to}
                onClick={() => setDrawerOpen(false)}
                selected={isNavItemActive(item.to)}
                sx={{
                  borderRadius: 1.5,
                  "&.Mui-selected": {
                    background: "color-mix(in srgb, var(--surface) 70%, var(--accent-cool) 30%)",
                  },
                  "&.Mui-selected:hover": {
                    background: "color-mix(in srgb, var(--surface) 62%, var(--accent-cool) 38%)",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isNavItemActive(item.to) ? 700 : 500,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                color="inherit"
                fullWidth
                onClick={() => {
                  colorMode?.toggleMode();
                  setDrawerOpen(false);
                }}
                startIcon={
                  colorMode?.mode === "dark" ? (
                    <LightModeRoundedIcon />
                  ) : (
                    <DarkModeRoundedIcon />
                  )
                }
              >
                {themeToggleLabel}
              </Button>
              <Tooltip title={t("common.nav.cookieSettings")}>
                <IconButton color="inherit" size="small" aria-label={t("common.nav.cookieSettings")}
                  onClick={() => { setDrawerOpen(false); void showCookiePreferences(); }}>
                  <CookieRoundedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <LanguageSelect compact />
          </Stack>
        </Box>
      </Drawer>
    </AppBar>
  );
}
